// Internationalization Manager - Handles translations
export interface Translations {
  [key: string]: string;
}

export class I18nManager {
  private currentLanguage = "en";
  private translations: Record<string, Translations> = {};
  private listeners: ((language: string) => void)[] = [];

  constructor() {
    // Detect browser language if no preference is stored
    const storedLanguage = localStorage.getItem("language");
    if (storedLanguage) {
      this.currentLanguage = storedLanguage;
    } else {
      // Detect browser language
      const browserLang = this.detectBrowserLanguage();
      this.currentLanguage = browserLang;
      localStorage.setItem("language", browserLang);
    }
    this.loadTranslations();
  }

  async init(): Promise<void> {
    await this.loadTranslations();
    // Ensure document language is set after translations load
    this.updateDocumentLanguage();
  }

  // Detect browser language
  private detectBrowserLanguage(): string {
    // Check navigator.languages (preferred order)
    if (navigator.languages && navigator.languages.length > 0) {
      for (const lang of navigator.languages) {
        const langCode = lang.split("-")[0].toLowerCase();
        if (langCode === "sv" || langCode === "en") {
          return langCode;
        }
      }
    }

    // Fallback to navigator.language
    if (navigator.language) {
      const langCode = navigator.language.split("-")[0].toLowerCase();
      if (langCode === "sv" || langCode === "en") {
        return langCode;
      }
    }

    // Default to English
    return "en";
  }

  // Load translations (bundled statically by esbuild)
  private async loadTranslations(): Promise<void> {
    try {
      const sv = (await import("../i18n/sv")).default;
      const en = (await import("../i18n/en")).default;
      this.translations["sv"] = sv;
      this.translations["en"] = en;
    } catch (error) {
      console.error("Failed to load translations:", error);
    }
  }

  // Set language
  setLanguage(language: string): void {
    // Always update even if same language (in case translations changed)
    this.currentLanguage = language;
    localStorage.setItem("language", language);
    this.updateDocumentLanguage();
    // Notify listeners AFTER updating document language
    this.notifyListeners();
  }

  // Get current language
  getLanguage(): string {
    return this.currentLanguage;
  }

  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  // Toggle between languages
  toggleLanguage(): void {
    const newLanguage = this.currentLanguage === "en" ? "sv" : "en";
    this.setLanguage(newLanguage);
  }

  // Translate text
  t(key: string, params?: Record<string, string | number>): string {
    const translation =
      this.translations[this.currentLanguage]?.[key] ||
      this.translations["en"]?.[key] ||
      key;

    if (params) {
      return this.interpolate(translation, params);
    }

    return translation;
  }

  // Interpolate parameters in translation
  private interpolate(
    text: string,
    params: Record<string, string | number>
  ): string {
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key]?.toString() || match;
    });
  }

  // Add language change listener
  onLanguageChange(listener: (language: string) => void): void {
    this.listeners.push(listener);
  }

  // Remove language change listener
  removeLanguageListener(listener: (language: string) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  // Notify listeners
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.currentLanguage);
      } catch (error) {
        console.error("Error in language listener:", error);
      }
    });
  }

  // Update document language
  private updateDocumentLanguage(): void {
    document.documentElement.lang = this.currentLanguage;

    // Update language toggle button
    const languageToggle = document.getElementById("language-toggle");
    if (languageToggle) {
      const languageText = languageToggle.querySelector(".language-text");
      if (languageText) {
        languageText.textContent = this.currentLanguage.toUpperCase();
      }
    }
  }

  // Get available languages
  getAvailableLanguages(): string[] {
    return Object.keys(this.translations);
  }

  // Check if translation exists
  hasTranslation(key: string, language?: string): boolean {
    const lang = language || this.currentLanguage;
    return this.translations[lang]?.[key] !== undefined;
  }

  // Get all translations for a language
  getTranslations(language?: string): Translations {
    const lang = language || this.currentLanguage;
    return this.translations[lang] || {};
  }

  // Format currency
  formatCurrency(amount: number, currency: string = "SEK"): string {
    const formatters: Record<string, Intl.NumberFormat> = {
      sv: new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency: "SEK",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
      en: new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "SEK",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    };

    const formatter = formatters[this.currentLanguage] || formatters["en"];
    return formatter.format(amount);
  }

  // Format date
  formatDate(date: Date | string | number): string {
    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;

    const formatters: Record<string, Intl.DateTimeFormat> = {
      sv: new Intl.DateTimeFormat("sv-SE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      en: new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    };

    const formatter = formatters[this.currentLanguage] || formatters["en"];
    return formatter.format(dateObj);
  }

  // Format number
  formatNumber(number: number): string {
    const formatters: Record<string, Intl.NumberFormat> = {
      sv: new Intl.NumberFormat("sv-SE"),
      en: new Intl.NumberFormat("en-US"),
    };

    const formatter = formatters[this.currentLanguage] || formatters["en"];
    return formatter.format(number);
  }

  // Get direction (LTR/RTL)
  getDirection(): string {
    return "ltr"; // Swedish and English are both LTR
  }

  // Update page direction
  updateDirection(): void {
    document.documentElement.dir = this.getDirection();
  }
}
